import { useEffect, useMemo, useRef, useState } from "react";
import { api } from "../api";
import { GiveUp } from "../components/GiveUp";
import { ResultSheet } from "../components/ResultSheet";
import { Centered, Header, Shell } from "../components/Shell";
import { Toast } from "../components/Toast";
import { haptic } from "../telegram";
import { useGame } from "../useGame";

interface View {
  n: number;
  started: boolean;
  regions?: number[];
  elapsed?: number;
  solution?: number[];
  seconds?: number | null;
}

const COPY = { not_started: "Спершу натисни «почати»", bad_input: "Щось не так з розстановкою" };

/** Region palette — pastel enough for dark glyphs in both themes. */
const PALETTE = ["#f2a3a3", "#f6c48f", "#f2e394", "#a8dba0", "#93d6cf", "#9db8f0", "#c3a6ee", "#eda4cf", "#c9c9c9"];

const fmt = (sec: number) => `${Math.floor(sec / 60)}:${String(sec % 60).padStart(2, "0")}`;

/** 0 = empty, 1 = ✕ marker, 2 = queen. */
type Cell = 0 | 1 | 2;

function conflictsOf(n: number, regions: number[], board: Cell[]): Set<number> {
  const bad = new Set<number>();
  const queens: number[] = [];
  for (let i = 0; i < board.length; i++) if (board[i] === 2) queens.push(i);
  for (const a of queens) {
    for (const b of queens) {
      if (a >= b) continue;
      const ax = a % n;
      const ay = Math.floor(a / n);
      const bx = b % n;
      const by = Math.floor(b / n);
      const clash =
        ay === by || ax === bx || regions[a] === regions[b] || (Math.abs(ax - bx) <= 1 && Math.abs(ay - by) <= 1);
      if (clash) {
        bad.add(a);
        bad.add(b);
      }
    }
  }
  return bad;
}

export function Queens({ date }: { date?: string | undefined }) {
  const g = useGame<View>("queens", COPY, date);
  const [board, setBoard] = useState<Cell[] | null>(null);
  const [tick, setTick] = useState(0);
  const baseElapsed = useRef(0);
  const loadedAt = useRef(Date.now());

  const state = g.screen.kind === "game" ? g.screen.state : null;
  const finished = state !== null && state.status !== "playing";
  const v = state?.view ?? null;

  useEffect(() => {
    if (v?.started && v.regions && board === null) {
      setBoard(new Array<Cell>(v.n * v.n).fill(0));
      baseElapsed.current = v.elapsed ?? 0;
      loadedAt.current = Date.now();
    }
  }, [v?.started, v?.regions, v?.n, board]);

  useEffect(() => {
    if (!v?.started || finished) return;
    const id = window.setInterval(() => setTick((t) => t + 1), 1000);
    return () => window.clearInterval(id);
  }, [v?.started, finished]);
  void tick;

  const conflicts = useMemo(
    () => (v?.regions && board ? conflictsOf(v.n, v.regions, board) : new Set<number>()),
    [v?.regions, v?.n, board],
  );

  const start = () => void g.run(() => api.guess<View>("queens", "start", date));

  const tap = (i: number) => {
    if (!board || finished || g.busy) return;
    haptic.tap();
    const next = [...board];
    next[i] = ((next[i] as number) + 1) % 3 as Cell;
    setBoard(next);
    // all queens down, nothing clashes → that's the solve, send it
    if (v?.regions) {
      const queens = next.filter((c) => c === 2).length;
      if (queens === v.n && conflictsOf(v.n, v.regions, next).size === 0) {
        const cols = new Array<number>(v.n).fill(-1);
        next.forEach((c, j) => {
          if (c === 2) cols[Math.floor(j / v.n)] = j % v.n;
        });
        void g.run(() => api.guess<View>("queens", `q:${cols.join(",")}`, date));
      }
    }
  };

  if (g.screen.kind === "loading") return <Shell><Centered>…</Centered></Shell>;
  if (g.screen.kind === "error") {
    return (
      <Shell>
        <Centered>
          <div>{g.screen.message}</div>
          {g.screen.retry && (
            <button type="button" onClick={() => void g.load()} className="rounded-xl bg-tgbutton px-5 py-2.5 text-sm font-semibold text-tgbuttontext">
              ще раз
            </button>
          )}
        </Centered>
      </Shell>
    );
  }
  const s = g.screen.state;
  const view = s.view;
  const won = s.status === "won";
  const elapsed = baseElapsed.current + Math.floor((Date.now() - loadedAt.current) / 1000);

  const renderBoard = (regions: number[], content: (i: number) => string, extraClass?: (i: number) => string) => {
    const n = view.n;
    const px = Math.min(44, Math.floor(330 / n));
    return (
      <div className="mx-auto" style={{ width: n * px }}>
        {Array.from({ length: n }, (_, y) => (
          <div key={y} className="flex">
            {Array.from({ length: n }, (_, x) => {
              const i = y * n + x;
              const reg = regions[i] as number;
              const bt = y === 0 || regions[i - n] !== reg ? "2px solid #222" : "1px solid rgba(0,0,0,0.15)";
              const bl = x === 0 || regions[i - 1] !== reg ? "2px solid #222" : "1px solid rgba(0,0,0,0.15)";
              const bb = y === n - 1 ? "2px solid #222" : "none";
              const br = x === n - 1 ? "2px solid #222" : "none";
              return (
                <button
                  key={x}
                  type="button"
                  onClick={() => tap(i)}
                  className={`flex items-center justify-center text-[#1a1a1a] ${extraClass?.(i) ?? ""}`}
                  style={{
                    width: px,
                    height: px,
                    background: PALETTE[reg % PALETTE.length],
                    borderTop: bt,
                    borderLeft: bl,
                    borderBottom: bb,
                    borderRight: br,
                    fontSize: px * 0.5,
                  }}
                >
                  {content(i)}
                </button>
              );
            })}
          </div>
        ))}
      </div>
    );
  };

  return (
    <Shell header={<Header left={date ? `📼 ${date}` : `#${s.number}`} title="КОРОЛЕВИ" right={date ? "" : `🔥 ${s.me.streak}`} />}>
      <div className="flex-1 overflow-y-auto px-4 pb-2">
        {!view.started && !finished ? (
          <Centered>
            <div className="text-5xl">👑</div>
            <div className="max-w-[300px] text-sm leading-relaxed text-tghint">
              Постав {view.n} королев: по одній в кожному рядку, стовпці й кольорі. Королеви не можуть торкатись - навіть
              кутами. Час пішов з кнопки.
            </div>
            <button
              type="button"
              onClick={start}
              disabled={g.busy}
              className="rounded-xl bg-tgbutton px-8 py-3 text-base font-bold text-tgbuttontext active:brightness-90 disabled:opacity-40"
            >
              почати ⏱
            </button>
          </Centered>
        ) : (
          <>
            <div className="my-2 text-center text-sm text-tghint">
              {finished ? (
                view.seconds != null ? `час: ${fmt(view.seconds)}` : "розв'язок"
              ) : (
                <>⏱ {fmt(elapsed)} · тап: пусто → ✕ → 👑</>
              )}
            </div>
            {view.regions &&
              (finished
                ? renderBoard(view.regions, (i) =>
                    view.solution && view.solution[Math.floor(i / view.n)] === i % view.n ? "👑" : "",
                  )
                : board
                  ? renderBoard(
                      view.regions,
                      (i) => (board[i] === 2 ? "👑" : board[i] === 1 ? "✕" : ""),
                      (i) => (conflicts.has(i) ? "ring-2 ring-inset ring-red-600" : ""),
                    )
                  : null)}
            {!finished && conflicts.size > 0 && (
              <div className="mt-2 text-center text-xs text-[#e05a4e]">королеви конфліктують</div>
            )}
          </>
        )}
      </div>

      {view.started && !finished && (
        <div className="flex px-4 pb-[calc(var(--tg-safe-area-inset-bottom,0px)+10px)]">
          <GiveUp busy={g.busy} onGiveUp={() => void g.run(() => api.giveUp<View>("queens", date))} />
        </div>
      )}

      <Toast message={g.toast} />
      {g.sheetOpen && finished && (
        <ResultSheet
          state={s}
          archive={Boolean(date)}
          title={won ? "Корона твоя!" : "Здався"}
          subtitle={
            won ? `час ${view.seconds != null ? fmt(view.seconds) : "?"} · стрік ${s.me.streak} 🔥` : "стрік згорів"
          }
          scoreLabel="avg час"
          formatScore={(n) => (n === null ? "–" : fmt(Math.round(n)))}
          onDismiss={() => g.setSheetOpen(false)}
        />
      )}
    </Shell>
  );
}
